import { produce } from 'immer'

import { assertUnreachable } from '@/shared/lib/types'

import type { ChannelEvent, ChannelEventOfType, Message } from '../api/protocol'
import type { ChannelItem, ChannelItemOfType } from './types'

export function processEvent(items: ChannelItem[], event: ChannelEvent): ChannelItem[] {
    switch (event.type) {
        case 'message_published': {
            const { optimistic_id, ...message } = event.payload

            return produce(items, (items) => {
                // replace optimistic message with real one
                if (optimistic_id) {
                    for (const item of items) {
                        if (isConfirmationMessage(item, optimistic_id)) {
                            item.payload = message
                            return
                        }
                    }
                }

                // add new message
                items.push({
                    type: 'message',
                    payload: message,
                })
            })
        }

        case 'message_hidden':
            // remove message from history
            return items.filter((item) => item.type !== 'message' || item.payload.id !== event.payload.message_id)

        case 'message_hidden_many': {
            // remove messages from history
            const hidden = new Set(event.payload.message_id_list)

            return items.filter((item) => item.type !== 'message' || !hidden.has(item.payload.id))
        }

        // reaction events are handled separately
        case 'message_reaction_added':
        case 'message_reaction_removed':
            return items

        default:
            assertUnreachable(event)
    }
}

// prepend incoming messages to history and remove duplicates
export function addHistory(items: ChannelItem[] | undefined, messages: Message[]) {
    const messageIds = new Set(items?.flatMap((item) => (item.type === 'message' ? item.payload.id : [])))
    const history = messages
        // filter out messages that are already in history
        .filter((message) => !messageIds.has(message.id))
        //  convert messages to channel items
        .map<ChannelItem>(({ optimistic_id, ...message }) => ({
            type: 'message',
            payload: message,
        }))

    return history.concat(items ?? [])
}

export function addChannelItem(channel: string, items: Record<string, ChannelItem[]>, item: ChannelItem) {
    return produce(items, (items) => {
        items[channel] = (items[channel] ?? []).concat(item)
    })
}

export function isMessagePublishEvent(payload: {
    channel: string
    event: ChannelEvent
}): payload is { channel: string; event: ChannelEventOfType<'message_published'> } {
    return payload.event.type === 'message_published'
}

function isConfirmationMessage(item: ChannelItem, optimisticId: string): item is ChannelItemOfType<'message'> {
    return item.type === 'message' && Boolean(item.payload.optimistic_id) && item.payload.optimistic_id === optimisticId
}
